<template>
  <transition
    :leave-active-class="`animated ${animationOut} fast`"
    :enter-active-class="`animated ${animationIn} fast`"
  >
    <div
      v-if="showMobileFirstLvl"
      v-click-outside="onClickOutside"
      class="mobile-navigation-first-level-fi"
    >
      <div class="mobile-navigation-first-level-fi__header">
        <a
          v-if="homeData && logoHeader"
          :href="homeData.url"
          :title="homeData.title"
          data-test="logo"
          class="mobile-navigation-first-level-fi__header__logo"
        >
          <img
            :src="logoHeader.src"
            :alt="logoHeader.alt"
            :title="logoHeader.title"
            class="fischer-brand-logo"
          />
        </a>

        <div
          class="mobile-navigation-first-level-fi__header__close"
          data-test="hide-flyout"
          @click="hideFlyout"
        >
          <icon-fi
            icon="close"
            size="2"
          />
        </div>
      </div>
      <ul class="mobile-navigation-first-level-fi__list">
        <template v-for="(item, index) in navigationData || []">
          <li
            v-if="!item.desktopOnly"
            :key="item.url"
            :class="{
              'mobile-navigation-first-level-fi__list__item': true,
              'mobile-navigation-first-level-fi__list__item--last':
                navigationData && index === navigationData.length - 1,
              'mobile-navigation-first-level-fi__list__item--active': item.isActiveSelfOrDescendant,
              'mobile-navigation-first-level-fi__list__item--focus': index === navIndex,
            }"
            :data-test-2="item.label"
            :data-test-3="isProducts(item) ? 'products' : undefined"
            data-test="navigation"
          >
            <div
              v-if="item.subElements.length"
              class="mobile-navigation-first-level-fi__list__item__wrapper"
              @click="showSecondLevel(item, index)"
            >
              <span class="mobile-navigation-first-level-fi__list__item__label">
                {{ item.label }}
              </span>

              <icon-fi icon="arrow-right" />
            </div>
            <a
              v-else
              :target="item.target || undefined"
              :href="item.url"
              class="mobile-navigation-first-level-fi__list__item__wrapper"
            >
              <span class="mobile-navigation-first-level-fi__list__item__label">
                {{ item.label }}
              </span>

              <icon-fi icon="arrow-right" />
            </a>
          </li>
        </template>

        <template v-for="navigation in serviceNavigation">
          <li
            v-for="navigationElement in navigation.elements"
            :key="`${navigation.title}_${navigationElement.identifier}`"
            class="mobile-navigation-first-level-fi__list__item mobile-navigation-first-level-fi__list__item--icon"
          >
            <a
              :href="
                navigationElement.link && navigationElement.identifier !== 'selectCountry'
                  ? navigationElement.link.url
                  : undefined
              "
              :aria-label="navigationElement.title"
              :data-test-2="navigationElement.identifier"
              data-test="navigation"
              class="mobile-navigation-first-level-fi__list__item__wrapper"
              @click="(evt) => onMobileNavigationClicked(navigationElement, evt)"
            >
              <span class="mobile-navigation-first-level-fi__list__item--icon--label">
                <icon-fi
                  :icon="iconMappings[navigationElement.identifier as IconMappingKeys]"
                  :alt="navigationElement.title"
                />
                {{ mobileNavigationTitle(navigationElement) }}
              </span>
              <icon-fi icon="arrow-right" />
            </a>
          </li>
        </template>
      </ul>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { IconFi } from 'atoms';
// @ts-expect-error - click-outside-vue3 has no type declarations
import vClickOutside from 'click-outside-vue3';
import { computed, nextTick, ref, watch } from 'vue';

import iconMappings, {
  type IconMappingKeys,
} from '@/components/header-fi/header-top-fi/service-navigation-item-fi/iconMappings';
import { useBodyScroll } from '@/composables/bodyScroll';
import { useBreakpoints } from '@/composables/breakpoint';
import { useCountrySelect } from '@/composables/countrySelect';
import { useFischer } from '@/composables/fischerPlugin';
import { useNavigation } from '@/composables/navigation';
import type { SitecoreNavigation } from '@/sitecoreState';
import { useNavigationStore } from '@/store/pinia/navigation';

defineOptions({
  name: 'MobileNavigationFirstLevelFi',
  directives: {
    clickOutside: vClickOutside.directive,
  },
});

interface ServiceNavigationElement {
  identifier: IconMappingKeys | string;
  title: string;
  link?: {
    url: string;
    text: string;
  };
}

export interface ServiceNavigationGroup {
  title: string;
  elements: ServiceNavigationElement[];
}

interface LogoHeader {
  src?: string;
  alt?: string;
  title?: string;
}

interface HomeData {
  url?: string;
  title?: string;
}

interface Props {
  serviceNavigation: ServiceNavigationGroup[];
  logoHeader?: LogoHeader;
  homeData?: HomeData | null;
  navigationData?: SitecoreNavigation[] | null;
}

withDefaults(defineProps<Props>(), {
  logoHeader: () => ({}),
  homeData: null,
  navigationData: null,
});

const navigationStore = useNavigationStore();
const fischer = useFischer();
const { isSM } = useBreakpoints();
const { bodyEnableScroll } = useBodyScroll();
const { isSingleLocale, getLocaleData, countrySelectUrl } = useCountrySelect();
const { showMobileSubNavigation } = useNavigation();

const isShown = ref(false);
const animationOut = ref('mobileNavigationOut');
const animationIn = ref('mobileNavigationIn');

const navIndex = computed(() => navigationStore.navIndex);
const showMobileFirstLvl = computed(() => navigationStore.showMobileFirstLvl);
const showMobileSubLvl = computed(() => navigationStore.showMobileSubLvl);

watch(showMobileFirstLvl, (newVal) => {
  isShown.value = newVal;
});

watch(showMobileSubLvl, (newVal) => {
  if (!newVal && !isShown.value) {
    animationOut.value = 'mobileNavigationOut';
    animationIn.value = 'mobileNavigationIn';
  }
});

const isProducts = (item: SitecoreNavigation) => {
  return item.url === fischer.pages.products().url;
};

const hideFlyout = async () => {
  animationOut.value = 'mobileNavigationOut';
  animationIn.value = 'mobileNavigationIn';

  bodyEnableScroll();
  navigationStore.setShowBrandArea(true);

  // timeout needed for animation change
  await nextTick();
  navigationStore.setShowMobileFirstLvl(false);
};

const showSecondLevel = async (item: SitecoreNavigation, index: number) => {
  if (!isSM.value) {
    animationOut.value = 'mobileNavigationFirstToSecond';
    animationIn.value = 'mobileNavigationSecondToFirst';
  }

  navigationStore.setSubElements(item.subElements);
  navigationStore.setGroupData(item);
  navigationStore.setNavIndex(index);
  navigationStore.setIsProducts(isProducts(item));
  navigationStore.setIsCompany(item.url === fischer.pages.companies().url);

  // timeout needed for animation change
  await nextTick();
  showMobileSubNavigation(isSM.value);
};

const onClickOutside = () => {
  if (isSM.value) {
    hideFlyout();
  }
};

const onMobileNavigationClicked = async (
  navigationElement: ServiceNavigationElement,
  evt: MouseEvent,
) => {
  const { identifier } = navigationElement;
  const isCountrySelectElement = identifier === 'selectCountry';

  if (!isCountrySelectElement) {
    return;
  }

  evt.preventDefault();
  evt.stopImmediatePropagation();

  await getLocaleData();

  if (isSingleLocale.value) {
    const url = navigationElement?.link?.url;
    const targetUrl = countrySelectUrl(url);
    if (targetUrl) {
      window.location.href = targetUrl;
    } else {
      navigationStore.setShowMobileFirstLvl(false);
    }

    return;
  }

  navigationStore.setIsCountrySelect(isCountrySelectElement);
  showMobileSubNavigation(isSM.value);
};

const mobileNavigationTitle = (navigationElement: ServiceNavigationElement) => {
  return navigationElement.link &&
    navigationElement.identifier !== 'login' &&
    navigationElement.identifier !== 'myAccount'
    ? navigationElement.link.text
    : navigationElement.title;
};
</script>

<style scoped src="./mobile-navigation-first-level-fi" />
